
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Pause, Play } from "lucide-react";
import { Product } from "@/types/product";
import ProductTableRow from "./ProductTableRow";

interface ProductTableProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onToggleStatus: (product: Product) => void;
}

const ProductTable = ({ products, onEdit, onDelete, onToggleStatus }: ProductTableProps) => {
  return (
    <div className="bg-card text-card-foreground rounded-lg border border-border overflow-hidden shadow-sm">
      {/* Versão Mobile: Cards sem necessidade de rolar para o lado para editar, pausar ou excluir */}
      <div className="md:hidden divide-y divide-border">
        {products.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Nenhum produto encontrado
          </div>
        ) : (
          products.map((product) => (
            <div key={product.id} className="p-4 space-y-3">
              <div className="flex gap-3 items-start">
                <div className="w-16 h-16 rounded overflow-hidden shrink-0 bg-muted border border-border">
                  <img
                    src={product.image_url || "/placeholder.svg"}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/placeholder.svg";
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-foreground text-base truncate">
                      {product.name}
                    </p>
                    <Badge variant={product.is_active ? "default" : "destructive"} className="shrink-0 text-xs">
                      {product.is_active ? "Ativo" : "Pausado"}
                    </Badge>
                  </div>
                  {product.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                      {product.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground border border-border">
                      {product.category}
                    </span>
                    <span className="font-bold text-sm text-foreground">
                      R$ {product.price.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Botões de Ações diretamente acessíveis na tela sem rolagem */}
              <div className="grid grid-cols-3 gap-2 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(product)}
                  className="border-border hover:bg-accent text-foreground h-9 px-1 text-xs"
                >
                  <Edit className="h-3.5 w-3.5 mr-1" />
                  <span>Editar</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onToggleStatus(product)}
                  className={`h-9 px-1 text-xs ${
                    product.is_active 
                      ? "border-orange-600/70 hover:bg-orange-500/10 text-orange-600 dark:text-orange-400 dark:border-orange-700 dark:hover:bg-orange-900"
                      : "border-green-600/70 hover:bg-green-500/10 text-green-600 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-900"
                  }`}
                >
                  {product.is_active ? <Pause className="h-3.5 w-3.5 mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                  <span>{product.is_active ? "Pausar" : "Reativar"}</span>
                </Button>
                <Button
                  variant="outline" 
                  size="sm"
                  onClick={() => onDelete(product)}
                  className="border-red-600/70 hover:bg-red-500/10 text-red-600 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900 h-9 px-1 text-xs"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  <span>Excluir</span>
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Versão Desktop: Tabela completa */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-muted-foreground">Imagem</TableHead>
              <TableHead className="text-muted-foreground">Nome</TableHead>
              <TableHead className="text-muted-foreground">Categoria</TableHead>
              <TableHead className="text-muted-foreground">Preço</TableHead>
              <TableHead className="text-muted-foreground">Status</TableHead>
              <TableHead className="text-muted-foreground">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Nenhum produto encontrado
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => (
                <ProductTableRow
                  key={product.id}
                  product={product}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onToggleStatus={onToggleStatus}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ProductTable;
